/**
 * English text is the key; Spanish comes from every messages.es.json in the app (shell, contract
 * and each module), downloaded only when someone reads in Spanish. Use t() for every visible string and useLanguage() in
 * components so a language switch re-renders without losing form state.
 */
import { createInstance } from "i18next";
import { useSyncExternalStore } from "react";

export type Language = "en" | "es";

const catalogs = import.meta.glob<Record<string, string>>(["../../**/messages.es.json"], { import: "default" });
let spanishLoad: Promise<void> | null = null;
let spanishReady = false;

const KEY = "hondo.v2.language";

function initial(): Language {
  try {
    const linked = new URLSearchParams(location.search).get("lang");
    if (linked === "es" || linked === "en") return linked;
    const saved = localStorage.getItem(KEY);
    if (saved === "es" || saved === "en") return saved;
  } catch { /* Storage is optional. */ }
  return (navigator.languages?.[0] ?? navigator.language ?? "en").toLowerCase().startsWith("es") ? "es" : "en";
}

const i18n = createInstance();
void i18n.init({
  lng: typeof window === "undefined" ? "en" : initial(), fallbackLng: "en", supportedLngs: ["en", "es"],
  resources: { en: { translation: {} }, es: { translation: {} } },
  keySeparator: false, nsSeparator: false, initAsync: false, returnEmptyString: false, interpolation: { escapeValue: false },
});

export const getLanguage = (): Language => (i18n.language === "es" ? "es" : "en");
export const getLocale = (): string => (getLanguage() === "es" ? "es-MX" : "en-US");

/** Translate an English catalog key. Plurals: pass count and keep "_one"/"_other" keys in the catalog. */
export function t(message: string, values?: Record<string, unknown>): string {
  if (values) {
    // Dates passed as ISO strings (from server messages and facts) read in the person's language.
    const local: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) local[k] = typeof v === "string" ? localizeDates(v) : v;
    values = local;
  }
  return i18n.t(message, { ...values, defaultValue: message });
}

const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
/** Replace ISO calendar dates inside text with short local dates ("Nov 9", "9 nov"; year only when not this year). */
export function localizeDates(text: string): string {
  if (!text.includes("-")) return text;
  const thisYear = new Date().getFullYear();
  return text.replace(ISO_DATE, (whole, y: string, m: string, d: string) => {
    const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), 12));
    if (Number.isNaN(date.getTime()) || date.getUTCMonth() !== Number(m) - 1) return whole;
    return date.toLocaleDateString(getLanguage() === "es" ? "es-MX" : "en-US", { month: "short", day: "numeric", ...(Number(y) === thisYear ? {} : { year: "numeric" }), timeZone: "UTC" });
  });
}
/** Alias for module code that shadows `t`. */
export const tr = t;

/** Singular/plural with English fallbacks: plural(n, "{{count}} cow", "{{count}} cows"). */
export function plural(count: number, one: string, other: string): string {
  return t(count === 1 ? one : other, { count });
}

const subscribe = (listener: () => void) => { i18n.on("languageChanged", listener); return () => i18n.off("languageChanged", listener); };
export function useLanguage(): Language {
  return useSyncExternalStore(subscribe, getLanguage, () => "en");
}

/** The installed app's name and description follow the language too. */
function syncDocument(language: Language): void {
  document.documentElement.lang = language;
  document.querySelector('link[rel="manifest"]')?.setAttribute("href", language === "es" ? "/manifest.es.webmanifest" : "/manifest.webmanifest");
}

/** Fetch every Spanish catalog once. English needs nothing: its text is the key. */
export function loadSpanish(): Promise<void> {
  spanishLoad ??= Promise.all(Object.values(catalogs).map((load) => load())).then((parts) => {
    i18n.addResourceBundle("es", "translation", Object.assign({}, ...parts), true, true);
    spanishReady = true;
    if (i18n.language === "es") void i18n.changeLanguage("es");
  });
  return spanishLoad;
}

/** Resolves when the starting language can render (the app waits on it before first paint). */
export const languageReady = (): Promise<void> => (typeof window !== "undefined" && i18n.language === "es" ? loadSpanish() : Promise.resolve());

export function setLanguage(language: Language): void {
  try { localStorage.setItem(KEY, language); } catch { /* Keep working without storage. */ }
  if (language === "es" && !spanishReady) {
    void loadSpanish().then(() => { void i18n.changeLanguage("es"); syncDocument("es"); });
    return;
  }
  if (language !== getLanguage()) void i18n.changeLanguage(language);
  syncDocument(language);
}

if (typeof window !== "undefined") {
  syncDocument(getLanguage());
  window.addEventListener("storage", (e) => { if (e.key === KEY && (e.newValue === "en" || e.newValue === "es")) setLanguage(e.newValue); });
}
